/**
 * O que fazer quando a primeira tela não consegue nascer.
 *
 * POR QUE ISTO EXISTE (07/10/2026)
 *   O projeto de dados pausou por inatividade e o usuário abriu o Fincast numa TELA PRETA,
 *   sem frase nenhuma. O caminho: `desenhar()` chamava `obterEstadoSessao()` fora de
 *   qualquer `try`; ela lança quando o servidor não responde; e quem chamava `desenhar()`
 *   o fazia com `void`, sem `.catch()`. A rejeição não tinha dono, o `#raiz` ficava vazio,
 *   e com o tema escuro isso é um retângulo preto. F5 não ajudava: a sessão guardada leva
 *   o app direto ao mesmo caminho.
 *
 *   O pior não era a falha — servidor fora do ar acontece, e o de demonstração VAI pausar
 *   de novo. O pior era a falha não dizer o que é. A pessoa não tem como saber se o
 *   problema é ela, a rede, ou o sistema.
 *
 * Este módulo é PURO de propósito (sem DOM): a regra "falha ao abrir vira tela com frase"
 * precisa de teste, e os testes rodam em Node. Quem desenha é o `main.ts`.
 */
import { ehFalhaDeConexao } from '../../../packages/dados/src/conexao';

export interface FalhaDeArranque {
  /** A rede ou o servidor não responderam — não é a pessoa que errou nada. */
  servidorFora: boolean;
  titulo: string;
  mensagem: string;
}

/**
 * Procura sinal de queda de rede no erro E nas causas que ele carrega.
 *
 * Olhar só o erro de cima não basta: `sessao.falhar()` embrulha o erro do Supabase num
 * `ErroFincast` (código `ERRO_SUPABASE`) e guarda o original em `causa.erro`. O texto de
 * cima até costuma trazer o "Failed to fetch", mas basta uma tradução para PT-BR para ele
 * sumir — e aí uma queda de servidor viraria "erro desconhecido". A profundidade é
 * limitada para uma causa circular não virar laço.
 */
export function ehServidorFora(erro: unknown, profundidade = 0): boolean {
  if (erro === null || erro === undefined || profundidade > 4) return false;
  if (ehFalhaDeConexao(erro)) return true;
  if (typeof erro !== 'object') return false;
  const o = erro as { causa?: unknown; cause?: unknown; erro?: unknown };
  return [o.causa, o.cause, o.erro].some((c) => ehServidorFora(c, profundidade + 1));
}

/** Frase de tela para a falha. Nunca cita ID nem detalhe técnico quando é queda. */
export function classificarFalhaDeArranque(erro: unknown): FalhaDeArranque {
  if (ehServidorFora(erro)) {
    return {
      servidorFora: true,
      titulo: 'O Fincast não está conseguindo falar com o servidor',
      mensagem:
        'Pode ser a sua conexão, ou o servidor pode estar fora do ar — o ambiente de demonstração '
        + 'entra em pausa quando fica dias sem uso. Tente de novo em alguns minutos; se continuar, '
        + 'avise quem administra o sistema.',
    };
  }
  const texto = erro instanceof Error ? erro.message : String(erro ?? '');
  return {
    servidorFora: false,
    titulo: 'Não foi possível abrir o Fincast',
    mensagem: texto.trim() !== '' ? texto : 'Erro desconhecido.',
  };
}

/**
 * Roda `desenho` e GARANTE que uma falha chegue a `aoFalhar` — classificada.
 *
 * É a regra que fecha a tela preta: nenhuma rejeição do desenho fica sem dono. Nunca
 * lança: o que der errado dentro do próprio `aoFalhar` também é contido, porque um erro
 * ali reabriria exatamente o buraco que esta função existe para fechar.
 */
export async function protegido(
  desenho: () => Promise<void>,
  aoFalhar: (falha: FalhaDeArranque, erro: unknown) => void,
): Promise<void> {
  try {
    await desenho();
  } catch (erro) {
    try {
      aoFalhar(classificarFalhaDeArranque(erro), erro);
    } catch {
      /* sem saída melhor que esta: o console já recebeu o erro original em `aoFalhar` */
    }
  }
}
